"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { FaCrown, FaCheckCircle } from "react-icons/fa";
import { useAuthStore } from "@/store/authStore";
import PageLoading from "@/components/PageLoading";

export default function SubscriptionPlansPage() {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [purchasing, setPurchasing] = useState(false);
    const [alertMsg, setAlertMsg] = useState(null);
    const [userCount, setUserCount] = useState(null);
    const [activeSubscription, setActiveSubscription] = useState(null);
    const [showPlans, setShowPlans] = useState(false);
    const router = useRouter();
    const { isAuthenticated, checkAuth } = useAuthStore();
    const [authChecked, setAuthChecked] = useState(false);
    const [paymentState, setPaymentState] = useState(null);
    const [paymentNotice, setPaymentNotice] = useState("");
    const [paymentChecksFinished, setPaymentChecksFinished] = useState(false);


    useEffect(() => {
        checkAuth().finally(() => setAuthChecked(true));
    }, []);

    useEffect(() => {
        if (authChecked && !isAuthenticated) router.push("/auth");
    }, [authChecked, isAuthenticated, router]);

   
    useEffect(() => {
        if (!authChecked || !isAuthenticated) return;

        let disposed = false;
        let timerId;
        let resolveDelay;

        const controller = new AbortController();

        const params = new URLSearchParams(window.location.search);
        const paymentReferenceId = params.get("referenceId");

        // هنگام cleanup، انتظار دوثانیه‌ای هم آزاد می‌شود.
        function waitBeforeNextCheck() {
            return new Promise((resolve) => {
                resolveDelay = resolve;

                timerId = window.setTimeout(() => {
                    resolveDelay = undefined;
                    resolve();
                }, 2000);
            });
        }

        async function fetchPlans() {
            const res = await api("/api/v1/subscription/plans", {
                method: "GET",
                cache: "no-store",
                signal: controller.signal,
            });

            if (disposed) return;

            const payload = res.data || {};
            const plansData = payload.plans || [];

            setPlans(plansData);
            setUserCount(payload.user_count ?? null);
            setActiveSubscription(payload.active_subscriptions || null);

            const monthlyPlan = plansData.find(
                (plan) => plan.slug == "subscription-monthly"
            );

            setSelectedPlan((current) =>
                plansData.some((plan) => plan.slug == current)
                    ? current
                    : monthlyPlan?.slug || plansData[0]?.slug || null
            );
        }

        async function loadPage() {
            setLoading(true);
            setPaymentChecksFinished(false);
            setPaymentState(null);
            setPaymentNotice("");

            let finalStatus = null;

            try {
                if (paymentReferenceId) {
                    setPaymentState("pending");
                    setPaymentNotice(
                        "در حال پردازش پرداخت شما هستیم؛ لطفاً صفحه را رفرش نکنید."
                    );

                    for (let attempt = 0; attempt < 4; attempt += 1) {
                        if (attempt > 0) {
                            await waitBeforeNextCheck();
                        }

                        if (disposed) return;
                        try {
                            const response = await api(
                                `/api/v1/payments/${encodeURIComponent(paymentReferenceId)}/status`,
                                {
                                    method: "GET",
                                    cache: "no-store",
                                    signal: controller.signal,
                                }
                            );

                            if (disposed) return;

                            const status = response.data?.status;

                            if (status == "done" || status == "cancelled") {
                                finalStatus = status;
                                window.history.replaceState(
                                    {},
                                    "",
                                    window.location.pathname + window.location.hash
                                );
                                break;
                            }
                        } catch (error) {
                            if (disposed) return;

                            console.error(
                                "Failed to check payment status:",
                                error
                            );
                        }
                    }

                    if (disposed) return;

                    setPaymentChecksFinished(true);
                    setPaymentState(finalStatus || "pending");

                    if (finalStatus == "done") {
                        setPaymentNotice(
                            "پرداخت تأیید شد؛ در حال دریافت وضعیت اشتراک..."
                        );
                    } else if (finalStatus == "cancelled") {
                        setPaymentNotice("پرداخت لغو یا ناموفق بود.");
                    } else {
                        setPaymentNotice(
                            "وضعیت پرداخت هنوز نهایی نشده است. کمی بعد دوباره بررسی کنید."
                        );
                    }
                }

                // پس از نتیجهٔ نهایی یا تمام شدن چهار تلاش،
                // اطلاعات اشتراک دریافت می‌شود.
                await fetchPlans();

                if (disposed) return;

                if (finalStatus == "done") {
                    setPaymentNotice("پرداخت شما با موفقیت تأیید شد.");
                }
            } catch (error) {
                if (disposed) return;

                console.error("Failed to load subscription plans:", error);

                if (paymentReferenceId) {
                    setPaymentNotice(
                        finalStatus == "done"
                            ? "پرداخت تأیید شد، اما دریافت اطلاعات اشتراک ناموفق بود. لطفاً دوباره صفحه را باز کنید."
                            : "دریافت اطلاعات اشتراک ناموفق بود. لطفاً دوباره صفحه را باز کنید."
                    );
                } else {
                    setAlertMsg({
                        text: "دریافت اطلاعات اشتراک ناموفق بود. لطفاً دوباره امتحان کنید.",
                        type: "error",
                    });
                }
            } finally {
                if (!disposed) {
                    // در تمام مسیرها، loading پایان پیدا می‌کند.
                    setLoading(false);
                    setPaymentChecksFinished(true);
                }
            }
        }

        void loadPage();

        return () => {
            disposed = true;
            controller.abort();
            window.clearTimeout(timerId);
            resolveDelay?.();
        };
    }, [authChecked, isAuthenticated]);


   

    function showAlert(text, type = "error") {
        setAlertMsg({ text, type });
        setTimeout(() => setAlertMsg(null), 4000);
    }

    async function handlePurchase() {
        if (!selectedPlan || purchasing) return;

        setPurchasing(true);
        setAlertMsg(null);

        try {
            const res = await api("/api/v1/account/deposit", {
                method: "POST",
                body: JSON.stringify({
                    agent: "web",
                    product_id: selectedPlan,
                    purchased_type: "subscription",
                }),
            });

            if (res.status == "ok") {
                window.location.href = res.pay_link;
                return;
            }

            showAlert(res.message, "error");
            setPurchasing(false);
        } catch {
            showAlert("خطایی رخ داده لطفا دوباره امتحان کنید.", "error");
            setPurchasing(false);
        }
    }

    // تا زمانی که بررسی احراز هویت تمام نشده، نمایش Loading
    if (!authChecked || !isAuthenticated) {
        return <PageLoading />;
    }

    // تا زمانی که دیتا لود نشده، نمایش Spinner
    if (loading) {
        return (
            <>
                <Navbar />

                <div
                    className="p-5 md:max-w-xl md:mx-auto"
                    dir="rtl"
                >
                    <div
                        role="status"
                        aria-live="polite"
                        className="rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-5 text-center text-sm text-yellow-200"
                    >
                        <span
                            aria-hidden="true"
                            className="mb-3 inline-block h-6 w-6 animate-spin rounded-full border-2 border-yellow-400 border-t-transparent"
                        />

                        <p>
                            {paymentNotice || "در حال دریافت اطلاعات اشتراک..."}
                        </p>
                    </div>
                </div>
            </>
        );
    }


    // تعیین محتوای نمایشی: اگر اشتراک فعال دارد و کاربر روی تمدید کلیک نکرده
    const shouldShowActiveSubscription = activeSubscription?.has_active && !showPlans;

    return (
        <>
            <Navbar showBack />         
            <div className="p-5 md:max-w-xl md:mx-auto text-white">
                {paymentNotice && (
                    <div
                        role="status"
                        className={`mb-5 rounded-lg border p-4 text-center text-sm ${paymentState == "done"
                            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                            : paymentState == "cancelled"
                                ? "border-red-500/30 bg-red-500/10 text-red-300"
                                : "border-yellow-500/30 bg-yellow-500/10 text-yellow-200"
                            }`}
                    >
                        {!paymentChecksFinished && paymentState !== "cancelled" && (
                            <span className="mb-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent align-middle" />
                        )}

                        <p>{paymentNotice}</p>
                    </div>
                )}

                {shouldShowActiveSubscription ? (
                    // نمایش اشتراک فعال
                    <>
                        <div className="relative flex items-center justify-center mb-8">

                            <div className="text-center">
                                <div className="inline-block bg-yellow-600/20 p-4 rounded-full mb-2">
                                    <FaCrown className="text-3xl text-yellow-500" />
                                </div>
                                <h2 className="text-xl font-black mb-1">
                                    عضو <span className="text-yellow-500">باشگاه طلایی</span> هستید!
                                </h2>
                            </div>
                        </div>

                        <div className="bg-gray-800 rounded-2xl p-5 mb-4 border border-yellow-500/30 shadow-[0_0_20px_2px_rgba(234,179,8,0.1)]">
                            <div className="flex items-center gap-3 mb-4">
                                <FaCheckCircle className="text-emerald-400 text-xl flex-shrink-0" />
                                <span className="text-emerald-400 font-bold">اشتراک فعال</span>
                            </div>
                            <p className="text-yellow-400 font-black text-2xl mb-1">
                                {activeSubscription?.remaining?.text}{" "}
                                <span className="text-gray-400 text-sm">تا پایان اشتراک شما</span>
                            </p>
                        </div>

                        <div className="flex flex-col gap-2 mb-8">
                            {activeSubscription?.plans?.map((plan, index) => (
                                <div key={index} className="flex items-start gap-3 bg-gray-800/50 rounded-xl px-4 py-3">
                                    <FaCheckCircle className="text-yellow-500 mt-1 flex-shrink-0" />
                                    <div className="flex flex-col gap-1">
                                        <div className="text-sm font-medium text-white">{plan.name}</div>
                                        <div className="text-[12px] text-gray-500 uppercase tracking-wider">
                                            <span className="text-gray-400">
                                                از {plan.starts_at} تا {plan.ends_at}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button
                            onClick={() => setShowPlans(true)}
                            className="w-full py-3 rounded-xl border-2 border-yellow-500/50 text-yellow-400 font-bold hover:bg-yellow-500/10 transition-all duration-200 text-xl"
                        >
                            تمدید عضویت
                        </button>
                    </>
                ) : (
                    // نمایش لیست پلن‌ها
                    <>
                        <div className="relative flex items-center justify-center mb-5">
                            <div className="text-center px-5">
                                <div className="inline-block bg-yellow-600/20 p-4 rounded-full mb-2">
                                    <FaCrown className="text-3xl text-yellow-500" />
                                </div>
                                <h2 className="text-xl font-black mb-2">
                                    عضو <span className="text-yellow-500">باشگاه طلایی</span> قصه شب شوید!
                                </h2>
                                <p className="text-gray-400 text-xs">
                                    دسترسی نامحدود به ۵۰۰+ داستان صوتی، ترانه و لالایی
                                </p>
                            </div>
                        </div>

                        {alertMsg && (
                            <div
                                className={`mb-4 p-3 rounded-lg text-sm text-center ${alertMsg.type == "error"
                                    ? "bg-red-500/10 border border-red-500/30 text-red-400"
                                    : "bg-emerald-500/10 border border-emerald-500/30 text-emerald-400"
                                    }`}
                            >
                                {alertMsg.text}
                            </div>
                        )}

                        <div className="flex flex-col gap-4">
                            {plans.map((plan) => {
                                const isSelected = selectedPlan == plan.slug;

                                return (
                                    <div
                                        key={plan.slug}
                                        onClick={() => !purchasing && setSelectedPlan(plan.slug)}
                                        className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all duration-200
            ${isSelected
                                                ? "border-yellow-500 bg-gray-800 shadow-[0_0_16px_2px_rgba(234,179,8,0.25)] scale-[1.02]"
                                                : "border-gray-700 bg-gray-900 hover:border-gray-500"
                                            }
            ${purchasing ? "opacity-60 pointer-events-none" : ""}`}
                                        dir="rtl"
                                    >
                                        <div className="flex items-start gap-3 flex-shrink-0">
                                            <div
                                                className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200
                    ${isSelected
                                                        ? "border-yellow-400"
                                                        : "border-gray-400"
                                                    }`}
                                            >
                                                <div
                                                    className={`rounded-full transition-all duration-200
                        ${isSelected
                                                            ? "w-3 h-3 bg-yellow-400"
                                                            : "w-0 h-0 bg-transparent"
                                                        }`}
                                                />
                                            </div>

                                            <div className="flex flex-col items-start gap-1">
                                                <h3
                                                    className={`font-bold transition-all duration-200 ${isSelected ? "text-yellow-400" : "text-white"}`}
                                                    style={{ fontSize: "clamp(0.85rem, 3.5vw, 1rem)" }}
                                                >
                                                    {plan.name}
                                                </h3>

                                                <span
                                                    className={`text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2 py-0.5 rounded-full font-medium ${plan.discount_percent > 0 ? "" : "invisible"}`}
                                                    style={{ fontSize: "clamp(0.6rem, 2.5vw, 0.75rem)" }}
                                                >
                                                    {plan.discount_percent > 0 ? `${plan.discount_percent}٪ تخفیف` : "٪ تخفیف"}
                                                </span>
                                            </div>
                                        </div>

                                        <span
                                            className={`font-black ${isSelected ? "text-yellow-400" : ""}`}
                                            style={{ fontSize: "clamp(0.9rem, 4vw, 1.25rem)" }}
                                        >
                                            {plan.price.toLocaleString("fa-IR")}
                                        </span>
                                    </div>
                                );

                            })}
                        </div>

                        <div className="mt-8 flex flex-col items-center gap-3">
                            <p className="text-gray-400 text-sm">
                                تا امروز {userCount} خانواده به «قصه شب» اعتماد کرده‌اند 🌱
                            </p>
                            <button
                                onClick={handlePurchase}
                                disabled={!selectedPlan || purchasing}
                                className="w-full py-3 rounded-xl bg-yellow-500 hover:bg-yellow-400 active:bg-yellow-600
                                           text-gray-900 font-black transition-all duration-200
                                           disabled:opacity-40 disabled:cursor-not-allowed
                                           shadow-[0_4px_20px_rgba(234,179,8,0.3)]
                                           flex items-center justify-center gap-2"
                                style={{ fontSize: "clamp(0.9rem, 4vw, 1.05rem)" }}
                            >
                                {purchasing ? (
                                    <>
                                        <span className="w-4 h-4 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                                        در حال انتقال به درگاه...
                                    </>
                                ) : (
                                    "شروع عضویت"
                                )}
                            </button>
                            <p
                                className="text-gray-500 text-center leading-relaxed"
                                style={{ fontSize: "clamp(0.6rem, 2.5vw, 0.72rem)" }}
                            >
                                با ادامه خرید{" "}
                                <a
                                    href="#"
                                    className="text-gray-400 underline underline-offset-2 hover:text-yellow-500 transition-colors"
                                >
                                    قوانین و مقررات
                                </a>{" "}
                                را می‌پذیرم
                            </p>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
